import { appManager } from '@inpart-io/devtools';
import { type MutationTree } from 'vuex';

import { PERSISTENCE_KEY } from '@/constants/persistence.constants';

import { type IApplicationState, type IDirectoryAssetExpansion, type IDirectoryCompanyExpansion, type ISettingsExpansion } from './types';

export const STORE_PARTNER_STATUS = 'STORE_PARTNER_STATUS';
export const STORE_CURRENT_BRIEF_ID = 'STORE_CURRENT_BRIEF_ID';
export const STORE_RECENT_AGREEMENT_BOARD_ID = 'STORE_RECENT_AGREEMENT_BOARD_ID';
export const STORE_RECENT_ALLIANCE_BOARD_ID = 'STORE_RECENT_ALLIANCE_BOARD_ID';
export const STORE_RECENT_OBLIGATION_BOARD_ID = 'STORE_RECENT_OBLIGATION_BOARD_ID';
export const STORE_RECENT_COMPANY_BOARD_ID = 'STORE_RECENT_COMPANY_BOARD_ID';
export const STORE_RECENT_LEAD_BOARD_ID = 'STORE_RECENT_LEAD_BOARD_ID';
export const STORE_RECENT_MEETING_BOARD_ID = 'STORE_RECENT_MEETING_BOARD_ID';
export const STORE_CURRENT_LEAD_ID = 'STORE_CURRENT_LEAD_ID';
export const STORE_MEETING_LIST_VIEW = 'STORE_MEETING_LIST_VIEW';
export const STORE_RECENT_VIEWED_LEAD_ID = 'STORE_RECENT_VIEWED_LEAD_ID';
export const STORE_RECENT_VIEWED_COMPANY_ID = 'STORE_RECENT_VIEWED_COMPANY_ID';
export const STORE_RECENT_VIEWED_BRIEF_ID = 'STORE_RECENT_VIEWED_BRIEF_ID';
export const STORE_RECENT_VIEWED_CAMPAIGN_ID = 'STORE_RECENT_VIEWED_CAMPAIGN_ID';
export const STORE_RECENT_LISTS = 'STORE_RECENT_LISTS';
export const STORE_SETTINGS_EXPANSION = 'STORE_SETTINGS_EXPANSION';
export const STORE_DIRECTORY_COMPANY_EXPANSION = 'STORE_DIRECTORY_COMPANY_EXPANSION';
export const STORE_DIRECTORY_ASSET_EXPANSION = 'STORE_DIRECTORY_ASSET_EXPANSION';
export const STORE_ALLOW_NAVIGATION = 'STORE_ALLOW_NAVIGATION';
export const STORE_EMAIL_PREVIEW_TEXT = 'STORE_EMAIL_PREVIEW_TEXT';

const trimList = (list: number[], max = 5): number[] => {
    return list?.length > 0 ? list.slice(0, max) : [];
};
const addToRecentList = (list: number[], toAdd: number, max: number): number[] => {
    if (list.includes(toAdd)) {
        list.splice(list.indexOf(toAdd), 1);
    }
    list.unshift(toAdd);
    return trimList(list, max);
};

export const mutations: MutationTree<IApplicationState> = {
    [STORE_ALLOW_NAVIGATION](_state, { key, add = true }: { key: string; add: boolean }) {
        if (key === null || key === undefined) {
            _state.allowNavigationSet.clear();
        } else {
            if (add) {
                _state.allowNavigationSet.add(key);
            } else {
                _state.allowNavigationSet.delete(key);
            }
        }
    },
    [STORE_PARTNER_STATUS](_state, partnerStatus: string) {
        _state.partnerStatus = partnerStatus;
        void appManager.persistenceManager.setItem(PERSISTENCE_KEY.PARTNER_STATUS, partnerStatus);
    },
    [STORE_CURRENT_BRIEF_ID](_state, briefId: number) {
        _state.currentBriefId = briefId;
        void appManager.persistenceManager.setItem(PERSISTENCE_KEY.CURRENT_BRIEF_ID, briefId);
    },
    [STORE_RECENT_COMPANY_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentCompanyBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_COMPANY_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_RECENT_LEAD_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentLeadBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_LEAD_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_RECENT_MEETING_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentMeetingBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_MEETING_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_RECENT_AGREEMENT_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentAgreementBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_AGREEMENT_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_RECENT_ALLIANCE_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentAllianceBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_ALLIANCE_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_RECENT_OBLIGATION_BOARD_ID](_state, { userKey, boardId }: { userKey: string; boardId: number }) {
        _state.recentObligationBoardId.set(userKey, boardId);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_OBLIGATION_BOARD_ID}.${userKey}`, boardId);
    },
    [STORE_EMAIL_PREVIEW_TEXT](_state, { userKey, emailPreviewText }: { userKey: string; emailPreviewText: boolean }) {
        _state.emailPreviewText = emailPreviewText;
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.EMAIL_PREVIEW_TEXT}.${userKey}`, emailPreviewText);
    },
    [STORE_MEETING_LIST_VIEW](_state, { userKey, view }: { userKey: string; view: string }) {
        _state.meetingListView.set(userKey, view);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.MEETING_LIST_VIEW}.${userKey}`, view);
    },
    [STORE_RECENT_LISTS](
        _state,
        {
            userKey,
            recentViewedLeadsIds,
            recentViewedCompaniesIds,
            recentViewedBriefsIds,
            recentViewedCampaignsIds
        }: {
            userKey: string;
            recentViewedLeadsIds: number[];
            recentViewedCompaniesIds: number[];
            recentViewedBriefsIds: number[];
            recentViewedCampaignsIds: number[];
        }
    ) {
        _state.recentViewedLeadsIds.set(userKey, recentViewedLeadsIds ?? []);
        _state.recentViewedCompaniesIds.set(userKey, recentViewedCompaniesIds ?? []);
        _state.recentViewedBriefsIds.set(userKey, recentViewedBriefsIds ?? []);
        _state.recentViewedCampaignsIds.set(userKey, recentViewedCampaignsIds ?? []);
    },
    [STORE_RECENT_VIEWED_LEAD_ID](_state, { leadId, userKey }: { leadId: number; userKey: string }) {
        const _recentLeads = _state.recentViewedLeadsIds.has(userKey) ? _state.recentViewedLeadsIds.get(userKey) : [];
        const recentLeads = addToRecentList(_recentLeads, leadId, _state.viewedMaxNb);
        _state.recentViewedLeadsIds.set(userKey, recentLeads);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_VIEWED_LEADS_IDS}.${userKey}`, recentLeads);
    },
    [STORE_RECENT_VIEWED_COMPANY_ID](_state, { companyId, userKey }: { companyId: number; userKey: string }) {
        const _recentCompaniesIds = _state.recentViewedCompaniesIds.has(userKey) ? _state.recentViewedCompaniesIds.get(userKey) : [];
        const recentCompanies = addToRecentList(_recentCompaniesIds, companyId, _state.viewedMaxNb);
        _state.recentViewedCompaniesIds.set(userKey, recentCompanies);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_VIEWED_COMPANIES_IDS}.${userKey}`, recentCompanies);
    },
    [STORE_RECENT_VIEWED_BRIEF_ID](_state, { briefId, userKey }: { briefId: number; userKey: string }) {
        const _recentBriefIds = _state.recentViewedBriefsIds.has(userKey) ? _state.recentViewedBriefsIds.get(userKey) : [];
        const recentBriefs = addToRecentList(_recentBriefIds, briefId, _state.viewedMaxNb * 2);
        _state.recentViewedBriefsIds.set(userKey, recentBriefs);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_VIEWED_BRIEFS_IDS}.${userKey}`, recentBriefs);
    },
    [STORE_RECENT_VIEWED_CAMPAIGN_ID](_state, { campaignId, userKey }: { campaignId: number; userKey: string }) {
        const _recentCampaignsIds = _state.recentViewedCampaignsIds.has(userKey) ? _state.recentViewedCampaignsIds.get(userKey) : [];
        const recentCampaigns = addToRecentList(_recentCampaignsIds, campaignId, _state.viewedMaxNb * 2);
        _state.recentViewedCampaignsIds.set(userKey, recentCampaigns);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.RECENT_VIEWED_CAMPAIGNS_IDS}.${userKey}`, recentCampaigns);
    },
    [STORE_SETTINGS_EXPANSION](_state, { expansion, userKey }: { expansion: ISettingsExpansion; userKey: string }) {
        _state.settingsExpansion.set(userKey, expansion);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.SETTINGS_EXPANSION}.${userKey}`, Object.assign({}, expansion));
    },
    [STORE_DIRECTORY_COMPANY_EXPANSION](_state, { expansion, userKey }: { expansion: IDirectoryCompanyExpansion; userKey: string }) {
        _state.directoryCompanyExpansion.set(userKey, expansion);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.DIRECTORY_COMPANY_EXPANSION}.${userKey}`, Object.assign({}, expansion));
    },
    [STORE_DIRECTORY_ASSET_EXPANSION](_state, { expansion, userKey }: { expansion: IDirectoryAssetExpansion; userKey: string }) {
        _state.directoryAssetExpansion.set(userKey, expansion);
        void appManager.persistenceManager.setItem(`${PERSISTENCE_KEY.DIRECTORY_ASSET_EXPANSION}.${userKey}`, Object.assign({}, expansion));
    }
};
